import React from "react";
import { Globe, Check } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTranslation, SUPPORTED_LOCALES } from "@/lib/i18n";

/**
 * LanguageSwitcher — clean, minimal locale toggle for the navbar.
 *
 * Variants:
 *   - "navbar" (default): pill-style ghost button with Globe icon + uppercase
 *     locale code, designed to sit next to other nav controls.
 *   - "compact":  small icon-only button (used on tight layouts / footer).
 *
 * The switcher is fully theme-aware and never hardcodes raw colors.
 * Selection is persisted in localStorage by the underlying i18n module.
 */
export default function LanguageSwitcher({ variant = "navbar" }) {
  const { t, locale, setLocale, locales } = useTranslation();
  const current = (locales || SUPPORTED_LOCALES).find((l) => l.code === locale) || SUPPORTED_LOCALES[0];

  const triggerClass =
    variant === "compact"
      ? "inline-flex items-center justify-center w-9 h-9 rounded-md hover:bg-secondary/60 transition-colors text-muted-foreground hover:text-foreground"
      // Phase 7.UI.7 — visually align with the NavLink siblings
      // (Watchlist / Pricing). Same h-9, px-3, rounded-md, hover:bg-secondary/60.
      // No border, no card-style fill — looks like a natural nav item.
      : "hidden sm:inline-flex h-9 px-3 items-center gap-2 rounded-md text-sm hover:bg-secondary/60 transition-colors";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={triggerClass}
          data-testid="language-switcher-trigger"
          aria-label={t("language.label")}
        >
          <Globe className="w-3.5 h-3.5 text-muted-foreground" />
          {variant === "navbar" ? (
            <>
              <span className="uppercase tracking-wider tabular text-sm" data-testid="language-switcher-current">
                {current.code}
              </span>
            </>
          ) : null}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44" data-testid="language-switcher-menu">
        <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
          {t("language.label")}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {(locales || SUPPORTED_LOCALES).map((l) => {
          const active = l.code === locale;
          return (
            <DropdownMenuItem
              key={l.code}
              onClick={() => setLocale(l.code)}
              data-testid={`language-switcher-option-${l.code}`}
              data-active={active ? "true" : undefined}
              className="cursor-pointer flex items-center gap-2"
            >
              <span aria-hidden className="text-base leading-none">{l.flag}</span>
              <span className="flex-1 text-sm">{l.label}</span>
              {active ? <Check className="w-3.5 h-3.5 text-[#3FAE89]" /> : null}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
